"use client";

import { useCallback, useEffect, useState, type CSSProperties } from "react";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { api, type Track, type NowPlaying, type RequestSummary, type StatsSample, type UpNextTrack } from "@/lib/api";
import { formatBitrate, formatBytes } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioPlayer } from "@/components/dashboard/radio-player";
import { StatCard, type Trend } from "@/components/dashboard/stat-card";
import { SortableRow } from "@/components/dashboard/sortable-row";
import { HomeExtras } from "@/components/dashboard/home-extras";
import { UpNextCard } from "@/components/dashboard/up-next-card";
import { AlbumArt } from "@/components/dashboard/album-art";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const HOUR = 3600;

function average(values: (number | null)[]): number | null {
  const real = values.filter((v): v is number => v !== null);
  return real.length ? real.reduce((a, b) => a + b, 0) / real.length : null;
}

// How much the last 5 minutes differ from the same 5 minutes an hour earlier.
// Null (no badge) while there isn't an hour of history yet, or nothing changed.
function hourlyTrend(samples: StatsSample[], pick: (s: StatsSample) => number | null): Trend | null {
  if (!samples.length) return null;
  const now = samples[samples.length - 1].t;
  const recent = average(samples.filter((s) => s.t > now - 300).map(pick));
  const before = average(samples.filter((s) => s.t > now - HOUR - 300 && s.t <= now - HOUR).map(pick));
  if (recent === null || before === null || before <= 0) return null;
  const pct = Math.round(((recent - before) / before) * 100);
  return pct === 0 ? null : { pct, title: "Compared with an hour ago" };
}

export default function DashboardHomePage() {
  const { can } = useAuth();
  // Cards and requests that need a permission are skipped without it.
  const canMedia = can("media.view");
  const canStats = can("statistics.view");
  const canRequests = can("requests.view");
  // How many listener requests wait for a decision (the same number as the red badge in the sidebar).
  const [requestSummary, setRequestSummary] = useState<RequestSummary | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [upNext, setUpNext] = useState<UpNextTrack[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Listener / bandwidth / storage samples (see api/app/core/sampler.py).
  const [samples, setSamples] = useState<StatsSample[]>([]);
  // The Now Playing card is kept exactly as tall as the Up Next card: measure
  // Up Next (it grows and shrinks with its list) and hand that to the player.
  const [upNextEl, setUpNextEl] = useState<HTMLDivElement | null>(null);
  const [upNextHeight, setUpNextHeight] = useState<number | null>(null);
  // "Now" for the library-growth maths, fixed when the page opens.
  const [now] = useState(() => Date.now());

  const loadTracks = useCallback(() => {
    api.listTracks().then(setTracks).catch(() => setLoadError("Couldn't load dashboard data from the API."));
  }, []);

  useEffect(() => {
    if (canMedia) loadTracks();
  }, [loadTracks, canMedia]);

  useEffect(() => {
    if (!canRequests) return;
    let cancelled = false;
    const load = () =>
      api
        .requestsSummary()
        .then((r) => !cancelled && setRequestSummary(r))
        .catch(() => {});
    const first = setTimeout(load, 0);
    const poll = setInterval(load, 15000);
    window.addEventListener("requests-changed", load);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(poll);
      window.removeEventListener("requests-changed", load);
    };
  }, [canRequests]);

  // Now Playing + Up Next poll on their own, shorter-lived cycle (matches
  // the sidebar's polling in layout.tsx) so the elapsed-time progress bar,
  // album art, and the upcoming queue stay current without reloading the
  // whole track list every few seconds.
  useEffect(() => {
    let cancelled = false;
    function poll() {
      Promise.all([api.nowPlaying(), api.upNext(5)])
        .then(([np, next]) => {
          if (!cancelled) {
            setNowPlaying(np);
            setUpNext(next);
          }
        })
        .catch(() => {});
    }
    poll();
    const interval = setInterval(poll, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!upNextEl) return;
    const observer = new ResizeObserver(() => setUpNextHeight(upNextEl.offsetHeight));
    observer.observe(upNextEl);
    return () => observer.disconnect();
  }, [upNextEl]);

  // The stat-card history refreshes at the sampler's own pace. Two hours, so
  // the "vs an hour ago" change has something to compare against.
  useEffect(() => {
    if (!canStats) return;
    let cancelled = false;
    function poll() {
      api
        .stats(120)
        .then((s) => !cancelled && setSamples(s.samples))
        .catch(() => {});
    }
    poll();
    const interval = setInterval(poll, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canStats]);

  const latest = samples.length ? samples[samples.length - 1] : null;
  // The sparklines and peaks cover the last hour.
  const lastHour = latest ? samples.filter((s) => s.t > latest.t - HOUR) : [];

  const listenerPeak = lastHour.reduce((max, s) => Math.max(max, s.listeners ?? 0), 0);
  const bandwidthPeak = lastHour.reduce((max, s) => Math.max(max, s.bandwidth_kbps ?? 0), 0);
  // Bandwidth needs two readings to work out, so the very latest sample can
  // be empty; fall back to the one before it rather than flashing "—".
  const bandwidthNow = latest?.bandwidth_kbps ?? samples[samples.length - 2]?.bandwidth_kbps ?? null;

  const listenersValue = !latest || latest.listeners === null ? "—" : latest.listeners.toLocaleString();
  const listenersSub = !latest
    ? "Collecting data…"
    : latest.listeners === null
      ? "Can't reach Icecast"
      : `Peak: ${listenerPeak.toLocaleString()}`;
  const bandwidthSub = !latest
    ? "Collecting data…"
    : bandwidthNow === null
      ? latest.online
        ? "Measuring…"
        : "Station offline"
      : `Peak: ${formatBitrate(bandwidthPeak)}`;

  const storageUsed = latest?.storage_bytes ?? null;
  const storageTotal = latest?.storage_total_bytes ?? null;
  const storagePercent = storageUsed !== null && storageTotal ? (storageUsed / storageTotal) * 100 : null;
  const storageValue =
    storageUsed === null
      ? "—"
      : storageTotal
        ? `${formatBytes(storageUsed)} / ${formatBytes(storageTotal)}`
        : formatBytes(storageUsed);
  const storageSub =
    storagePercent === null
      ? !latest
        ? "Collecting data…"
        : "Used in object storage"
      : `${storagePercent < 1 ? "<1" : Math.round(storagePercent)}% used`;

  // Library growth: running total of tracks by the day they were added (last
  // 14 days, ending today), plus how much it grew over the past week.
  const day = 86400000;
  const sparkStart = new Date(now).setHours(0, 0, 0, 0) - 13 * day;
  const createdAt = tracks.map((t) => new Date(t.created_at).getTime());
  const beforeSpark = createdAt.filter((c) => c < sparkStart).length;
  const perDay = Array.from({ length: 14 }, (_, d) =>
    createdAt.filter((c) => c >= sparkStart + d * day && c < sparkStart + (d + 1) * day).length
  );
  const trackSpark = perDay.map((_, d) => beforeSpark + perDay.slice(0, d + 1).reduce((a, b) => a + b, 0));
  const addedThisWeek = createdAt.filter((c) => c >= now - 7 * day).length;
  const tracksBeforeWeek = tracks.length - addedThisWeek;
  const tracksTrend: Trend | null =
    addedThisWeek > 0 && tracksBeforeWeek > 0
      ? { pct: Math.round((addedThisWeek / tracksBeforeWeek) * 100), title: "Library growth over the last 7 days" }
      : null;

  const recentTracks = [...tracks]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "#fff",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "h-6 w-6",
  };

  const statCards = [
    {
      id: "listeners",
      node: (
        <StatCard
          label="Listeners"
          value={listenersValue}
          sub={listenersSub}
          subTitle="Highest in the last hour"
          trend={hourlyTrend(samples, (s) => s.listeners)}
          spark={{ values: lastHour.map((s) => s.listeners ?? 0), color: "#9333ea" }}
          iconBg="bg-accent-purple"
          icon={
            // Headphones
            <svg {...iconProps}>
              <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
            </svg>
          }
        />
      ),
    },
    {
      id: "bandwidth",
      node: (
        <StatCard
          label="Bandwidth"
          value={formatBitrate(bandwidthNow)}
          sub={bandwidthSub}
          subTitle="Highest in the last hour"
          trend={hourlyTrend(samples, (s) => s.bandwidth_kbps)}
          spark={{ values: lastHour.map((s) => s.bandwidth_kbps ?? 0), color: "#0d9488" }}
          iconBg="bg-accent-teal"
          icon={
            <svg {...iconProps}>
              <path d="M12 20a8 8 0 1 0-8-8M12 12l4-4" />
            </svg>
          }
        />
      ),
    },
    {
      id: "storage",
      node: (
        <StatCard
          label="Storage"
          value={storageValue}
          sub={storageSub}
          subTitle="Used out of the server's disk"
          bar={storagePercent === null ? null : { percent: storagePercent, color: "#2563eb" }}
          iconBg="bg-accent-blue"
          icon={
            <svg {...iconProps}>
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5v14a9 3 0 0 0 18 0V5" />
              <path d="M3 12a9 3 0 0 0 18 0" />
            </svg>
          }
        />
      ),
    },
    {
      id: "requests",
      node: (
        <StatCard
          label="Requests"
          value={requestSummary ? String(requestSummary.pending) : "—"}
          sub={
            !requestSummary
              ? "Loading…"
              : requestSummary.pending > 0
                ? "waiting for review"
                : requestSummary.accepting
                  ? "none waiting"
                  : "requests are closed"
          }
          subTitle={requestSummary ? `${requestSummary.received_today} received today` : undefined}
          href="/requests"
          iconBg={requestSummary && requestSummary.pending > 0 ? "bg-accent-orange" : "bg-accent-green"}
          icon={
            <svg {...iconProps}>
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
            </svg>
          }
        />
      ),
    },
    {
      id: "tracks",
      node: (
        <StatCard
          label="Tracks"
          value={tracks.length.toLocaleString()}
          sub="Total Library"
          trend={tracksTrend}
          spark={{ values: trackSpark, color: "#ea580c", scale: "fit" }}
          iconBg="bg-accent-orange"
          icon={
            <svg {...iconProps}>
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          }
        />
      ),
    },
  ];

  const panels = [
    {
      id: "now-playing",
      node: (
        // Side by side (large screens) it matches Up Next's height; stacked on
        // small screens it just sizes to its content.
        <Card
          className="flex flex-col lg:min-h-[var(--up-next-height)]"
          style={{ "--up-next-height": upNextHeight ? `${upNextHeight}px` : "0px" } as CSSProperties}
        >
          <CardHeader className="justify-between">
            <CardTitle>Now Playing</CardTitle>
            {/* Station-is-broadcasting, independent of whether this
                browser tab is actually listening (that's RadioPlayer's
                own Play/Pause + "Listening in this tab" indicator). */}
            {nowPlaying?.on_air ? (
              <Badge variant="live" className="gap-1.5 font-normal">
                {/* Pulsing dot: a solid centre with a ring that expands and
                    fades, so it reads as "live". */}
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-green opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-green" />
                </span>
                ON AIR
              </Badge>
            ) : (
              <Badge variant="default">OFFLINE</Badge>
            )}
          </CardHeader>
          <CardContent className="flex flex-1 flex-col justify-center">
            <RadioPlayer nowPlaying={nowPlaying} />
          </CardContent>
        </Card>
      ),
    },
    {
      id: "up-next",
      node: (
        <div ref={setUpNextEl}>
          <UpNextCard nowPlaying={nowPlaying} upNext={upNext} />
        </div>
      ),
    },
    {
      id: "recent-tracks",
      node: (
        // Same height as Now Playing and Up Next side by side (long lists scroll
        // inside); stacked on small screens it just sizes to its content.
        <Card
          className="flex flex-col lg:h-[var(--up-next-height)]"
          style={{ "--up-next-height": upNextHeight ? `${upNextHeight}px` : "auto" } as CSSProperties}
        >
          <CardHeader className="shrink-0 justify-between">
            <CardTitle>Recent Tracks</CardTitle>
            <Link href="/media" className="mr-7 text-xs font-semibold text-accent-blue">
              View all →
            </Link>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto">
            {recentTracks.length === 0 ? (
              <p className="text-sm text-text-tertiary">No tracks uploaded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Track</TableHead>
                    <TableHead>Album</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTracks.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <AlbumArt trackId={t.id} hasCoverArt={!!t.cover_art_path} size="sm" />
                          <div className="min-w-0">
                            <div className="truncate font-medium">{t.title}</div>
                            <div className="truncate text-xs text-text-secondary">{t.artist}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-text-secondary">{t.album ?? "—"}</TableCell>
                      <TableCell className="text-text-secondary">{t.year ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={t.status === "ready" ? "live" : t.status === "error" ? "red" : "default"}>
                          {t.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {loadError && (
        <Card className="border-accent-red/30 bg-red-50">
          <CardContent className="py-3 text-sm text-accent-red">{loadError}</CardContent>
        </Card>
      )}

      <SortableRow
        storageKey="home-stat-cards"
        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        items={statCards.filter((c) => (c.id === "tracks" ? canMedia : c.id === "requests" ? canRequests : canStats))}
      />

      <SortableRow
        storageKey="home-panels"
        className="grid grid-cols-1 items-start gap-5 lg:grid-cols-3"
        items={panels.filter((p) => p.id !== "recent-tracks" || canMedia)}
      />

      <HomeExtras nowPlaying={nowPlaying} samples={samples} />
    </div>
  );
}
